import React, {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import type { Action, Location } from 'history';
import { useHistory } from 'react-router-dom';
import {
  UnsavedChangesProvider,
  useUnsavedChangesContext
} from './_private/UnsavedChangesContext';
import {
  UnsavedChangesDialog,
  UnsavedChangesDialogProps
} from './_private/UnsavedChangesDialog';
import {
  usePreventUnsavedChangesDrop,
  UsePreventUnsavedChangesDropOptions
} from './_private/usePreventUnsavedChangesDrop';

type PendingLeave =
  | { kind: 'navigation'; location: Location; action: Action }
  | { kind: 'dialog-close' };

export interface UnsavedChangesProtectionProps
  extends Pick<UsePreventUnsavedChangesDropOptions, 'beforeUnloadMessage'> {
  /**
   * Whether the form has unsaved changes
   */
  isDirty?: boolean;
  /**
   * Whether the dialog is open
   */
  open: boolean;
  /**
   * The original dialog onOpenChange handler
   */
  onOpenChange: (open: boolean) => void;
  /**
   * Protected content. Pass a node for pages, or a render prop when the dialog
   * needs the enhanced `onOpenChange` (blocks dismiss while dirty).
   */
  children:
    | ReactNode
    | ((props: {
        open: boolean;
        onOpenChange: (open: boolean) => void;
      }) => ReactNode);
  /**
   * Optional custom props for the UnsavedChangesDialog
   */
  dialogProps?: Partial<UnsavedChangesDialogProps>;
  /**
   * When this returns true for a target location, in-app navigation proceeds
   * without the unsaved-changes dialog (e.g. sibling tabs with shared state).
   * Use `createAllowedNavigationMatcher` from `v5/platform/react-router`.
   */
  allowNavigation?: (location: Location) => boolean;
}

/**
 * @experimental
 * Convenient wrapper for unsaved-changes protection (dialogs and pages).
 * Automatically renders the UnsavedChangesDialog and handles all the protection logic.
 * Also provides the UnsavedChangesContext to all children for managing multiple dirty states.
 *
 * While `open` and dirty: blocks in-app navigation (`history.block`), tab close
 * (`beforeunload`), and dialog dismiss (enhanced `onOpenChange` via render-prop children).
 *
 * For pages, prefer {@link PageUnsavedChangesProtection}.
 */
export const UnsavedChangesProtection: React.FC<
  UnsavedChangesProtectionProps
> = props => {
  return (
    <UnsavedChangesProvider>
      <UnsavedChangesProtectionInner {...props} />
    </UnsavedChangesProvider>
  );
};

const UnsavedChangesProtectionInner: React.FC<
  UnsavedChangesProtectionProps
> = ({
  isDirty,
  open,
  onOpenChange,
  children,
  dialogProps,
  allowNavigation,
  ...options
}) => {
  const history = useHistory();
  const { hasUnsavedChanges: hasUnsavedChangesInsideContext } =
    useUnsavedChangesContext();
  const finalDirty = isDirty || hasUnsavedChangesInsideContext;
  const {
    isDialogOpen,
    closeDialog,
    handleConfirm,
    handleCancel,
    triggerProtection
  } = usePreventUnsavedChangesDrop(finalDirty, {
    preventBeforeUnload: open,
    ...options
  });

  const pendingLeaveRef = useRef<PendingLeave | null>(null);
  const unblockNavigationRef = useRef<(() => void) | undefined>();
  const triggerProtectionRef = useRef(triggerProtection);
  triggerProtectionRef.current = triggerProtection;
  const allowNavigationRef = useRef(allowNavigation);
  allowNavigationRef.current = allowNavigation;

  const [navigationAllowed, setNavigationAllowed] = useState(false);

  useEffect(() => {
    if (!finalDirty) {
      setNavigationAllowed(false);
      pendingLeaveRef.current = null;
    }
  }, [finalDirty]);

  useEffect(() => {
    if (!open) {
      closeDialog();
      pendingLeaveRef.current = null;
    }
  }, [open, closeDialog]);

  const shouldBlockNavigation = open && finalDirty && !navigationAllowed;

  // useLayoutEffect so history.block is removed synchronously when open becomes
  // false (e.g. flushSync after programmatic close, before post-submit navigate).
  useLayoutEffect(() => {
    if (!shouldBlockNavigation) {
      return;
    }

    const unblock = history.block((location, action) => {
      if (allowNavigationRef.current?.(location)) {
        return;
      }

      pendingLeaveRef.current = { kind: 'navigation', location, action };
      triggerProtectionRef.current();

      // POP (back/forward): the URL updates before the blocker runs; undo it so
      // the address bar stays in sync until the user confirms in UnsavedChangesDialog.
      if (action === 'POP') {
        history.go(1);
      }

      return false;
    });

    unblockNavigationRef.current = unblock;

    return () => {
      unblock();
      unblockNavigationRef.current = undefined;
    };
  }, [shouldBlockNavigation, history]);

  const enhancedOnOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && finalDirty) {
      pendingLeaveRef.current = { kind: 'dialog-close' };
      triggerProtection();
    } else {
      onOpenChange(nextOpen);
    }
  };

  const handleConfirmLeave = () => {
    handleConfirm();
    const pending = pendingLeaveRef.current;
    pendingLeaveRef.current = null;

    if (pending?.kind === 'navigation') {
      unblockNavigationRef.current?.();
      unblockNavigationRef.current = undefined;
      setNavigationAllowed(true);
      onOpenChange(false);
      history.push({
        pathname: pending.location.pathname,
        search: pending.location.search,
        hash: pending.location.hash,
        state: pending.location.state
      });
      return;
    }

    if (pending?.kind === 'dialog-close') {
      onOpenChange(false);
    }
  };

  const handleCancelLeave = () => {
    pendingLeaveRef.current = null;
    handleCancel();
  };

  const protectedChildren =
    typeof children === 'function'
      ? children({ open, onOpenChange: enhancedOnOpenChange })
      : children;

  return (
    <>
      {/* Stable wrapper so Dialog is not remounted when open → false (exit animation). */}
      <div className="contents">{protectedChildren}</div>

      <UnsavedChangesDialog
        open={isDialogOpen}
        onOpenChange={handleCancelLeave}
        onConfirm={handleConfirmLeave}
        {...dialogProps}
      />
    </>
  );
};
